import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Gauge, Calendar, Cog, Fuel, Palette, Car, Loader2, MapPin } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StickyCallBar } from "@/components/StickyCallBar";
import { InquiryForm } from "@/components/InquiryForm";
import { Button } from "@/components/ui/button";
import { api, fileUrl, track, PHONE_DISPLAY, PHONE_TEL, MAP_URL, ADDRESS } from "@/lib/api";

const placeholder =
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1200&q=80";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    track("vehicle", id);
    api
      .get(`/vehicles/${id}`)
      .then((r) => {
        setVehicle(r.data);
        setActive(r.data.main_photo_index || 0);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="font-display font-bold text-2xl text-slate-900">Vehicle not found</h1>
          <p className="text-slate-500 mt-2">This vehicle may have been sold or removed.</p>
          <Button onClick={() => navigate("/inventory")} className="mt-6 bg-red-600 hover:bg-red-700 rounded-full">
            Back to Inventory
          </Button>
        </div>
        <Footer />
      </div>
    );

  if (!vehicle)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );

  const photos = vehicle.photos?.length ? vehicle.photos.map(fileUrl) : [placeholder];
  const label = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const isSold = vehicle.status === "sold";

  const specs = [
    { icon: Calendar, label: "Year", value: vehicle.year },
    { icon: Gauge, label: "Mileage", value: `${vehicle.mileage.toLocaleString()} km` },
    { icon: Car, label: "Body Type", value: vehicle.body_type },
    { icon: Cog, label: "Transmission", value: vehicle.transmission || "—" },
    { icon: Fuel, label: "Fuel", value: vehicle.fuel_type || "—" },
    { icon: Palette, label: "Color", value: vehicle.exterior_color || "—" },
    { icon: Car, label: "Drivetrain", value: vehicle.drivetrain || "—" },
  ];

  return (
    <div className="min-h-screen pb-16 md:pb-0" data-testid="vehicle-detail-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link to="/inventory" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-red-600 mb-4" data-testid="back-to-inventory-link">
          <ArrowLeft size={16} /> Back to inventory
        </Link>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Gallery */}
          <div>
            <div className="relative aspect-[16/11] rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
              <img src={photos[active]} alt={label} className="w-full h-full object-cover" data-testid="detail-main-photo" />
              {isSold && (
                <span className="absolute top-4 left-4 bg-rose-600 text-white text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full shadow-lg" data-testid="detail-sold-badge">
                  Sold
                </span>
              )}
            </div>
            {photos.length > 1 && (
              <div className="mt-3 grid grid-cols-6 gap-2">
                {photos.map((p, i) => (
                  <button
                    key={`${p}-${i}`}
                    onClick={() => setActive(i)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      active === i ? "border-red-600" : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                    data-testid={`detail-thumbnail-${i}`}
                  >
                    <img src={p} alt={`${label} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400">{vehicle.body_type}</p>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 mt-1">{label}</h1>
            <p className="text-3xl font-black text-red-600 mt-3" data-testid="detail-price">
              ${vehicle.price.toLocaleString()}
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`tel:${PHONE_TEL}`} className="flex-1 min-w-[160px]">
                <Button className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold h-12 rounded-full active:scale-95" data-testid="detail-call-button">
                  <Phone size={18} className="mr-2" /> Call {PHONE_DISPLAY}
                </Button>
              </a>
              <a href="#inquire" className="flex-1 min-w-[160px]">
                <Button variant="outline" className="w-full h-12 rounded-full font-semibold border-slate-300" data-testid="detail-inquire-scroll-button">
                  Inquire Online
                </Button>
              </a>
            </div>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {specs.map((s) => (
                <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-3">
                  <div className="flex items-center gap-1 text-slate-400 text-xs">
                    <s.icon size={14} /> {s.label}
                  </div>
                  <p className="font-semibold text-slate-900 text-sm mt-1">{s.value}</p>
                </div>
              ))}
            </div>

            {vehicle.description && (
              <div className="mt-8">
                <h2 className="font-display font-bold text-lg text-slate-900 mb-2">Description</h2>
                <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">{vehicle.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Inquiry */}
        <div id="inquire" className="mt-14 grid lg:grid-cols-2 gap-8 scroll-mt-20">
          <div>
            <h2 className="font-display font-bold text-2xl text-slate-900">Inquire About This Vehicle</h2>
            <p className="text-slate-500 mt-2 text-sm">
              Send us a message about the {label} and we'll get right back to you. Prefer to talk?{" "}
              <a href={`tel:${PHONE_TEL}`} className="text-red-600 font-semibold">Call {PHONE_DISPLAY}</a>.
            </p>
            <a href={MAP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 text-sm text-slate-600 hover:text-red-600">
              <MapPin size={16} /> {ADDRESS}
            </a>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <InquiryForm vehicleId={vehicle.id} vehicleLabel={label} />
          </div>
        </div>
      </div>
      <Footer />
      <StickyCallBar />
    </div>
  );
}
